import { useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

const GOOGLE_LOGO = "https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png";

export function GoogleReviews() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const { data, isLoading } = trpc.reviews.getReviews.useQuery();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-rotate reviews every 5 seconds
  useEffect(() => {
    if (!data?.reviews?.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % data.reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [data?.reviews?.length]);

  const reviews = data?.reviews || [];
  const overallRating = data?.overallRating;
  const totalReviews = data?.totalReviews;

  const next = () => {
    if (reviews.length) setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };
  const prev = () => {
    if (reviews.length) setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  // Render stars
  const renderStars = (rating: number) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-4 h-4 ${s <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
        />
      ))}
    </div>
  );

  return (
    <section className="py-16 bg-white" dir={isAr ? "rtl" : "ltr"}>
      <div className="container max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={GOOGLE_LOGO} alt="Google" className="h-6" />
            <span className="text-sm text-gray-500">
              {isAr ? "التقييمات" : "Reviews"}
            </span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {isAr ? "ماذا يقول مرضانا" : "What Our Patients Say"}
          </h2>
          {typeof overallRating === "number" && typeof totalReviews === "number" && (
            <div className="flex items-center justify-center gap-2 mt-3">
              {renderStars(overallRating)}
              <span className="text-lg font-semibold text-gray-800">{overallRating}/5</span>
              <span className="text-sm text-gray-500">
                ({totalReviews.toLocaleString()} {isAr ? "تقييم" : "reviews"})
              </span>
            </div>
          )}
        </div>

        {/* Reviews Carousel */}
        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-pulse bg-gray-100 rounded-xl h-48 w-full max-w-2xl" />
          </div>
        ) : reviews.length > 0 ? (
          <div className="relative max-w-3xl mx-auto">
            {/* Navigation Arrows */}
            <button
              onClick={prev}
              className="absolute left-1 md:left-0 top-1/2 -translate-y-1/2 md:-translate-x-12 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition"
              aria-label="Previous review"
            >
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
            </button>
            <button
              onClick={next}
              className="absolute right-1 md:right-0 top-1/2 -translate-y-1/2 md:translate-x-12 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition"
              aria-label="Next review"
            >
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
            </button>

            {/* Review Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-[#FBF8F1] to-white rounded-2xl p-8 shadow-sm border border-[#D4A843]/10"
              >
                <Quote className="w-8 h-8 text-[#D4A843]/30 mb-4" />
                <p className="text-gray-700 text-lg leading-relaxed mb-6 min-h-[80px]">
                  {reviews[currentIndex]?.text
                    ? reviews[currentIndex].text.length > 250
                      ? reviews[currentIndex].text.substring(0, 250) + "..."
                      : reviews[currentIndex].text
                    : ""}
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {reviews[currentIndex]?.authorName}
                    </p>
                    <div className="mt-1">
                      {renderStars(reviews[currentIndex]?.rating || 5)}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {reviews[currentIndex]?.time
                      ? new Date(reviews[currentIndex].time * 1000).toLocaleDateString(
                          isAr ? "ar-AE" : "en-AE",
                          { year: "numeric", month: "short" }
                        )
                      : ""}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex
                      ? "bg-[#D4A843] w-6"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Review ${i + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          /* Fallback when no reviews in DB yet */
          <div className="text-center text-gray-500">
            <p>{isAr ? "جاري تحميل التقييمات..." : "Loading reviews..."}</p>
          </div>
        )}

        {/* Google Badge */}
        <div className="text-center mt-8">
          <a
            href="https://maps.app.goo.gl/WLR3tdjmDChx5coh8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#D4A843] transition"
          >
            <img src={GOOGLE_LOGO} alt="Google" className="h-4" />
            {isAr ? "شاهد جميع التقييمات على جوجل" : "See all reviews on Google"}
          </a>
        </div>
      </div>
    </section>
  );
}
